import TextArea from "../../../../../Components/Common/TextArea/TextArea";
import { CHAR_LIMIT } from "../../../../../utils/Common/Constant";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  getGcProgress,
  updateGcProgress,
} from "../../../../../services/projectService";
import Skeleton from "../../../../../Components/Common/Skeleton/Skeleton";
import { useToast } from "../../../../../Components/Common/Toast/ToastContext";
import NoData from "Pages/Admin/InspectorAssign/components/NoData";
import Button from "Components/Common/Button/Button";

interface UpdateProgressProps {
  isActive: boolean;
  onShowSection?: (id: string) => void;
  projectId: string;
  cscCompanyName?: string;
  lenderCompanyName?: string;
}

type ProgressStatus = "not-started" | "in-progress" | "nearing" | "complete";

interface ProgressLineItem {
  id: string;
  numberLabel: string;
  description: string;
  subtext: string;
  status: ProgressStatus;
  note: string;
  flagged?: boolean;
}

const statusClassMap: Record<ProgressStatus, string> = {
  "not-started": "pu-not-started",
  "in-progress": "pu-in-progress",
  nearing: "pu-nearing",
  complete: "pu-complete",
};

const statusLabelMap: Record<ProgressStatus, string> = {
  "not-started": "Not Started",
  "in-progress": "In Progress",
  nearing: "Nearing Completion",
  complete: "Complete",
};

const fromApiStatus = (s: string): ProgressStatus => {
  if (s === "not_started") return "not-started";
  if (s === "in_progress") return "in-progress";
  if (s === "nearing_completion" || s === "nearing") return "nearing";
  if (s === "complete" || s === "completed") return "complete";
  return "not-started";
};

const toApiStatus = (s: ProgressStatus): string => {
  if (s === "not-started") return "not_started";
  if (s === "in-progress") return "in_progress";
  if (s === "nearing") return "nearing_completion";
  return "completed";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeItem = (raw: any, index: number): ProgressLineItem => {
  const lien = raw.lienItem ?? {};
  const aia = raw.aiaItem ?? {};
  const description = aia.itemName ?? lien.description ?? "";
  const subtextParts = [
    aia.itemCode != null ? String(aia.itemCode) : null,
    lien.subContractor?.companyName ?? null,
    lien.amount?.amount != null
      ? `SOV: $${Number(lien.amount.amount).toLocaleString()}`
      : null,
  ].filter(Boolean);
  return {
    id: raw.id ?? "",
    numberLabel: String(raw.displayOrder ?? index + 1),
    description,
    subtext: subtextParts.join(" — "),
    status: fromApiStatus(raw.status ?? "not_started"),
    note: raw.note ?? "",
    flagged: raw.flagged ?? false,
  };
};

const UpdateProgress = ({
  isActive,
  projectId,
  cscCompanyName = "CSC New England",
  lenderCompanyName = "the bank",
}: UpdateProgressProps) => {
  const showToast = useToast();
  const [items, setItems] = useState<ProgressLineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [savedMessage, setSavedMessage] = useState<string>("");
  const [submittedMessage, setSubmittedMessage] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!isActive || !projectId || fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    getGcProgress(projectId)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((res: any) => {
        const raw = Array.isArray(res?.data?.data?.items)
          ? res.data.data.items
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : Array.isArray(res?.data)
              ? res.data
              : [];
        setItems(raw.map(normalizeItem));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId, isActive]);

  const counts = useMemo(
    () =>
      items.reduce(
        (acc, item) => {
          acc[item.status] += 1;
          return acc;
        },
        { "not-started": 0, "in-progress": 0, nearing: 0, complete: 0 },
      ),
    [items],
  );

  const updateStatus = (id: string, status: ProgressStatus) =>
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, status } : item)),
    );

  const setAllStatuses = (status: ProgressStatus) =>
    setItems((prev) => prev.map((item) => ({ ...item, status })));

  const updateNote = (id: string, note: string) =>
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, note } : item)),
    );

  const buildPayload = () =>
    items.map((item) => {
      const entry: { id: string; status: string; note?: string } = {
        id: item.id,
        status: toApiStatus(item.status),
      };
      if (item.note.trim()) entry.note = item.note.trim();
      return entry;
    });

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await updateGcProgress(projectId, buildPayload(), false);
      const msg = res?.data?.message ?? "Draft saved";
      showToast(msg, "default");
      setSubmittedMessage("");
      setSavedMessage("Draft held - not yet submitted");
    } catch {
      showToast("Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await updateGcProgress(projectId, buildPayload(), true);
      const msg = res?.data?.message ?? "Progress updated";
      showToast(msg, "default");
      const now = new Date();
      const time = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
      setSubmittedMessage(`Last submitted: today at ${time}`);
      setSavedMessage(`Submitted ${time}`);
    } catch {
      showToast("Submission failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className={`section ${isActive ? "active" : ""}`.trim()}
      id="sec-progress"
    >
      <div className="sectionTitle">Update Construction Progress</div>
      <div className="sectionSub">
        Report your current status for each SOV line item. This is your
        self-reported progress — separate from any draw request. CSC and the
        bank can view this at any time.
      </div>

      <div className="puDisclaimer">
        <strong>Note:</strong>
        These statuses are visible to {cscCompanyName} and {lenderCompanyName}.
        They represent your own assessment of progress and are not a substitute
        for a CSC field inspection.
        {/* Last submitted: March 24, 2026 (with Draw #07). */}
      </div>

      <div className="puSummaryBar">
        <span className="puSumChip">
          <span className="puSumDot red" />
          <span>{counts["not-started"]} Not Started</span>
        </span>
        <span className="puSumChip">
          <span className="puSumDot amber" />
          <span>{counts["in-progress"]} In Progress</span>
        </span>
        <span className="puSumChip">
          <span className="puSumDot blue" />
          <span>{counts.nearing} Nearing Completion</span>
        </span>
        <span className="puSumChip">
          <span className="puSumDot green" />
          <span>{counts.complete} Complete</span>
        </span>
        <span className="puSavedMsg">{submittedMessage}</span>
      </div>

      <div className="card">
        <div className="sovHeader">
          <span className="cardSovTitle">SOV Line Item Status</span>
          <div className="puQuickSet">
            <span>{"Quick-set all:".toUpperCase()}</span>
            <Button
              className="btn secondary sm"
              type="button"
              onClick={() => setAllStatuses("not-started")}
            >
              Not Started
            </Button>
            <Button
              className="btn secondary sm"
              type="button"
              onClick={() => setAllStatuses("in-progress")}
            >
              In Progress
            </Button>
          </div>
        </div>

        {loading ? (
          <div>
            {Array.from({ length: 5 }).map((_, i) => (
              <div className="puItem" key={i}>
                <Skeleton width={24} height={24} borderRadius={4} />
                <div className="puInfo">
                  <Skeleton
                    width="55%"
                    height={13}
                    style={{ marginBottom: 6 }}
                  />
                  <Skeleton
                    width="40%"
                    height={11}
                    style={{ marginBottom: 10 }}
                  />
                  <div className="puBtns">
                    {Array.from({ length: 4 }).map((__, j) => (
                      <Skeleton
                        key={j}
                        width={100}
                        height={30}
                        borderRadius={6}
                      />
                    ))}
                  </div>
                  <Skeleton width="40%" height={11} style={{ marginTop: 10 }} />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <NoData text="No progress items found." />
        ) : (
          <div>
            {items.map((item) => (
              <div className="puItem" key={item.id} data-id={item.id}>
                <span className="puNum">{item.numberLabel}</span>
                <div className="puInfo">
                  <div className="puDesc">{item.description}</div>
                  <div className="puSub">{item.subtext}</div>
                  {item.flagged ? (
                    <div className="puFlag">
                      CSC flagged this item as overbilled. Update your status
                      and add a note explaining progress if it differs from
                      CSC&apos;s estimate.
                    </div>
                  ) : null}
                  <div className="puBtns">
                    {(Object.keys(statusClassMap) as ProgressStatus[]).map(
                      (statusKey) => (
                        <Button
                          key={`${item.id}-${statusKey}`}
                          type="button"
                          className={`pub ${item.status === statusKey ? statusClassMap[statusKey] : ""}`.trim()}
                          onClick={() => updateStatus(item.id, statusKey)}
                        >
                          {statusLabelMap[statusKey]}
                        </Button>
                      ),
                    )}
                  </div>
                  <TextArea
                    className="puNoteInput"
                    placeholder="Optional note for this item..."
                    rows={item.flagged ? 2 : 1}
                    value={item.note}
                    onChange={(e) => updateNote(item.id, e.target.value)}
                    maxChars={CHAR_LIMIT.DESCRIPTION}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {items.length !== 0 ? (
          <div className="puSaveBar">
            <div className="puSaveInfo">
              Updates are visible to {cscCompanyName} and {lenderCompanyName}{" "}
              immediately upon saving.
            </div>
            <div className="puSaveActions">
              <span className="puSavedCheck">
                {savedMessage ? `✓ ${savedMessage}` : ""}
              </span>
              <Button
                className="btn secondary"
                type="button"
                onClick={handleSaveDraft}
                disabled={saving}
              >
                Save Draft
              </Button>
              <Button
                className="btn primary"
                type="button"
                onClick={handleSubmit}
                disabled={saving}
              >
                Submit Progress Update
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default UpdateProgress;
